import { DecimalFormatOptions, IntegerFormatOptions, SYSTEM_DEFAULT_DECIMAL_FORMAT, SYSTEM_DEFAULT_INTEGER_FORMAT } from 'interfaces-lib';
import { FieldMetaTypeGuards } from '../interfaces/FieldMetaTypeGuards';
import { IFieldMeta } from '../interfaces/imetadata';
import { Adv2Config } from './adv2-config';
import { BullhornFieldMeta } from './bullhornfieldmeta';
import {
  DateFormatOptions,
  SortDirn,
  SortOption,
  StringFormatCapitalisation,
  StringFormatOptions,
  StringFormatSubstring,
} from './fieldconfig';

// local types
type DefaultSortOption = { field: string; dirn: SortDirn };
type IdentifierOption = { id: string; value: any };
type FormattingOptions = {
  dateFormatOptions: DateFormatOptions;
  integerFormat: Readonly<IntegerFormatOptions>;
  decimalFormat: Readonly<DecimalFormatOptions>;
  stringFormat: StringFormatOptions;
};
type FieldOptions = {
  formatting: FormattingOptions;
  sort: {
    byIdentifier: IdentifierOption[];
    byChild: { requiredFields: string[]; sort: DefaultSortOption }[];
  };
};

// the default "fallback" options for fields
export const systemDefaults: FieldOptions = {
  formatting: {
    dateFormatOptions: {
      dateFormat: 'MMM dd, yyyy',
      language: '',
    },
    integerFormat: SYSTEM_DEFAULT_INTEGER_FORMAT,
    decimalFormat: SYSTEM_DEFAULT_DECIMAL_FORMAT,
    stringFormat: {
      removeHtmlTags: false,
      substringType: StringFormatSubstring.NONE,
      substringFromStart: -1,
      substringFromEnd: -1,
      substringRangeStart: -1,
      substringRangeEnd: -1,
      capitalisation: StringFormatCapitalisation.NONE,
      renderHtml: false,
    },
  },
  // for 'to-many' fields only
  sort: {
    // exact match on id (e.g. field path)
    byIdentifier: [
      // TODO: setup config for namespace
      // { id: 'peopleListItem.education', value: {field: 'startDate', dirn: SortDirn.DSC} },
      // { id: 'peopleListItem.positions', value: {field: 'displayStartDate', dirn: SortDirn.DSC} },
      // { id: 'assignmentsListItem.candidates.education', value: {field: 'startDate', dirn: SortDirn.DSC} },
      // { id: 'assignmentsListItem.candidates.positions', value: {field: 'displayStartDate', dirn: SortDirn.DSC} },
    ],
    // match on the set of required fields (e.g. education has both startDate and endDate fields)
    byChild: [
      // order is important
      {
        requiredFields: ['startDate', 'endDate'],
        sort: { field: 'startDate', dirn: SortDirn.DSC },
      },
      {
        requiredFields: ['displayStartDate', 'displayEndDate'],
        sort: { field: 'displayStartDate', dirn: SortDirn.DSC },
      },
    ],
  },
};

export class FieldConfigHelper {
  constructor(private config: Adv2Config) {}

  getDateFormatOptions(fieldInstanceFormat?: DateFormatOptions): DateFormatOptions {
    if (fieldInstanceFormat) {
      return fieldInstanceFormat;
    }

    if (this.config?.dateFormat) {
      return {
        ...systemDefaults.formatting.dateFormatOptions,
        dateFormat: this.config.dateFormat,
      };
    }

    return { ...systemDefaults.formatting.dateFormatOptions };
  }

  getIntegerFormat(fieldInstanceFormat?: IntegerFormatOptions): IntegerFormatOptions {
    if (fieldInstanceFormat) {
      return fieldInstanceFormat;
    }

    // spread over the system defaults so a partial or legacy stored object can never produce
    // options with missing keys, which FormatIntegerComponent.isValid() would silently reject
    if (this.config?.integerFormat) {
      return {
        ...systemDefaults.formatting.integerFormat,
        ...this.config.integerFormat,
      };
    }

    return { ...systemDefaults.formatting.integerFormat };
  }

  getStringFormat(fieldMeta: IFieldMeta, fieldInstanceFormat?: StringFormatOptions): StringFormatOptions {
    if (fieldInstanceFormat) {
      return fieldInstanceFormat;
    }

    const defaults = { ...systemDefaults.formatting.stringFormat };
    if (FieldMetaTypeGuards.isBullhorn(fieldMeta)) {
      defaults.renderHtml = BullhornFieldMeta.mayContainHtml(fieldMeta);
    }
    return defaults;
  }

  getDecimalFormat(fieldInstanceFormat?: DecimalFormatOptions): DecimalFormatOptions {
    if (fieldInstanceFormat) {
      return fieldInstanceFormat;
    }

    // spread over the system defaults so a partial or legacy stored object can never produce
    // options with missing keys, which FormatDecimalComponent.isValid() would silently reject
    if (this.config?.decimalFormat) {
      return {
        ...systemDefaults.formatting.decimalFormat,
        ...this.config.decimalFormat,
      };
    }

    return { ...systemDefaults.formatting.decimalFormat };
  }

  getDefaultEmptyValue(fieldPath: string, fieldType: string, fieldInstanceDefaultEmptyValue?: string): string | undefined {
    if (fieldInstanceDefaultEmptyValue) {
      return fieldInstanceDefaultEmptyValue;
    }

    const defaultIdentifierValue = this.config?.emptyFields?.byIdentifier?.find((emptyField) => {
      return emptyField.key === fieldPath;
    })?.value;
    if (defaultIdentifierValue) {
      return defaultIdentifierValue;
    }

    const defaultTypeValue = this.config?.emptyFields?.byType?.find((emptyField) => {
      return emptyField.key === fieldType;
    })?.value;
    if (defaultTypeValue) {
      return defaultTypeValue;
    }

    // TODO: shouldn't we fall back to system defaults for each type? E.g. string='', integer=0, etc.
    return undefined;
  }

  getSortOption(field: IFieldMeta, fieldInstanceSortOption?: SortOption): SortOption | undefined {
    if (fieldInstanceSortOption) {
      return fieldInstanceSortOption;
    }

    if (field.type !== 'to-many') {
      return undefined;
    }

    // const identifiers = this.config?.sort.byIdentifier ?? systemDefaults.sort.byIdentifier;

    const fieldSpecific = systemDefaults.sort.byIdentifier.find((entry: IdentifierOption) => entry.id === field.path);
    if (fieldSpecific) {
      return fieldSpecific.value;
    }

    if (systemDefaults.sort.byChild.length === 0) {
      return undefined;
    }

    const childFields = field.fields?.filter((cf) => cf.type !== 'to-many');
    const childFieldNames = childFields?.map((cf) => cf.path.split('.').slice(-1)[0]);
    const childSpecific = systemDefaults.sort.byChild.find((entry: { requiredFields: string[]; sort: DefaultSortOption }) => {
      return entry.requiredFields.every((requiredField) => childFieldNames?.includes(requiredField));
    });

    if (!childSpecific) {
      return undefined;
    }

    const path = childFields?.find((field) => {
      const fieldName = field.path.split('.').slice(-1)[0];
      return fieldName === childSpecific?.sort.field;
    })?.path;

    if (!path) {
      return undefined;
    }

    return { ...childSpecific.sort, fieldPath: path };
  }
}
